/**
 * ErpSignal — fixed-position ERP (MS SQL) connectivity indicator.
 *
 * A small pill in the top-right of every page. Colour reflects live health:
 *   green   = ok      (latency < 800ms)
 *   amber   = degraded (latency ≥ 800ms OR running on fallback host)
 *   red     = down    (last /health/erp call failed)
 *   grey    = unknown (still checking on first load)
 *
 * On hover, a tooltip reveals: host IP, DB name, SQL server name, auth user,
 * round-trip latency, and whether the fallback is currently in use.
 *
 * Polls /api/health/erp every 30s. Cheap — a single `SELECT @@SERVERNAME`.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ErpSignal() {
  const [state, setState] = useState({ status: "unknown" });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/api/health/erp`, {
          timeout: 8000,
          // Never let a 503 boot the user via the global 401 interceptor.
          validateStatus: () => true,
        });
        if (!cancelled) setState(data || { status: "down" });
      } catch (e) {
        if (!cancelled) {
          setState({
            status: "down",
            host: "",
            error: e?.message || "unreachable",
          });
        }
      }
    };
    load();
    const id = setInterval(load, 30_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  // Colour map — Tailwind classes on both the dot and the ring.
  const colour = (() => {
    switch (state.status) {
      case "ok":
        return { dot: "bg-emerald-500", ring: "ring-emerald-300", label: "Connected" };
      case "degraded":
        return { dot: "bg-amber-500", ring: "ring-amber-300", label: "Degraded" };
      case "down":
        return { dot: "bg-rose-500 animate-pulse", ring: "ring-rose-300", label: "Down" };
      default:
        return { dot: "bg-slate-400", ring: "ring-slate-300", label: "Checking…" };
    }
  })();

  const isFallback = !!state.fallback_active;
  const displayHost = state.resolved_host || state.host || "—";

  return (
    <div
      data-testid="erp-signal-light"
      className="fixed top-2 right-2 z-[60] pointer-events-auto"
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`ERP ${colour.label}`}
            className={`flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur-sm
              border border-slate-200 shadow-sm px-2 py-1 text-[10px] font-medium
              text-slate-700 hover:shadow-md transition-shadow`}
          >
            <span
              className={`inline-block w-2.5 h-2.5 rounded-full ${colour.dot} ring-2 ring-offset-1 ring-offset-white ${colour.ring}`}
              data-testid="erp-signal-dot"
            />
            <span className="hidden sm:inline uppercase tracking-wider">ERP</span>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className="max-w-[280px] bg-slate-900 text-white text-[11px] leading-relaxed"
        >
          <div className="font-semibold text-emerald-300 mb-1">
            ERP · {colour.label}
            {isFallback && (
              <span className="ml-1 text-amber-300">(fallback)</span>
            )}
          </div>
          <Row label="Host" value={displayHost} mono />
          <Row label="Database" value={state.database} mono />
          {state.server_name && <Row label="SQL Server" value={state.server_name} mono />}
          {state.user && <Row label="User" value={state.user} mono />}
          {typeof state.latency_ms === "number" && (
            <Row label="Latency" value={`${state.latency_ms} ms`} />
          )}
          {state.status === "down" && state.error && (
            <div className="mt-1 pt-1 border-t border-slate-700 text-rose-300 break-words">
              {state.error}
            </div>
          )}
          {isFallback && (
            <div className="mt-1 pt-1 border-t border-slate-700 text-amber-200">
              Primary host unavailable — running on fallback route.
            </div>
          )}
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

function Row({ label, value, mono = false }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-3">
      <span className="text-slate-400">{label}</span>
      <span className={mono ? "font-mono text-white" : "text-white"}>{value}</span>
    </div>
  );
}
